import { useState } from "react";
import { motion } from "framer-motion";
import { Compass, FlaskConical, PenTool, Hammer, Rocket, LineChart } from "lucide-react";
import { DetailModal } from "@/components/site/DetailModal";

const STEPS = [
  {
    icon: Compass,
    num: "01",
    title: "DISCOVER",
    desc: "Understand the idea and the opportunity behind it.",
    accent: "#FF8800",
    detail:
      "Every engagement begins with deep discovery. LaunchLab sits with the founder to fully understand the idea, the motivation behind it, the market it targets and the opportunity it represents — mapping strengths, gaps and the true potential before anything is built.",
    bullets: ["Founder and idea deep-dive sessions", "Opportunity and market-size mapping", "Strengths, gaps and risk assessment"],
  },
  {
    icon: FlaskConical,
    num: "02",
    title: "VALIDATE",
    desc: "Test the market and the business potential.",
    accent: "#00B0FF",
    detail:
      "Next, the idea meets reality. LaunchLab tests customer demand, competitor dynamics, pricing tolerance and willingness to pay through structured experiments — producing a clear, evidence-backed verdict on whether and how to proceed.",
    bullets: ["Customer interviews and demand tests", "Competitor and pricing analysis", "Evidence-backed go / no-go verdict"],
  },
  {
    icon: PenTool,
    num: "03",
    title: "DESIGN",
    desc: "Build the business model and the strategy.",
    accent: "#00E676",
    detail:
      "With validation in hand, LaunchLab designs the venture on paper before it exists in the world — the business model, revenue architecture, brand strategy, technology plan and operating blueprint that everything else will follow.",
    bullets: ["Business model and revenue design", "Brand and technology strategy", "Operating blueprint and roadmap"],
  },
  {
    icon: Hammer,
    num: "04",
    title: "CREATE",
    desc: "Build the brand, the product and the technology.",
    accent: "#FF8800",
    detail:
      "This is where the business becomes real. LaunchLab's teams build the brand identity, develop the product or service, and engineer the websites, apps, platforms and software the venture will run on — all in parallel, all connected.",
    bullets: ["Brand identity and packaging build", "Product development and sourcing", "Website, app and software engineering"],
  },
  {
    icon: Rocket,
    num: "05",
    title: "LAUNCH",
    desc: "Take the business into the market.",
    accent: "#00B0FF",
    detail:
      "LaunchLab coordinates a synchronised market entry — brand campaigns, sales channels, operations and technology all going live together — then monitors the first weeks closely, fixing fast and amplifying what works.",
    bullets: ["Coordinated go-to-market execution", "Launch campaigns and PR", "Early-metrics monitoring and rapid fixes"],
  },
  {
    icon: LineChart,
    num: "06",
    title: "GROW",
    desc: "Improve, scale and expand.",
    accent: "#00E676",
    detail:
      "After launch, LaunchLab operates as a long-term growth partner — improving revenue and customer acquisition, upgrading technology and operations, and expanding distribution until the business is ready to scale across markets.",
    bullets: ["Growth and retention programs", "Systems and technology upgrades", "Distribution and market expansion"],
  },
];

export function HowItWorks() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="how-we-work" data-testid="how-it-works-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-14"
        >
          <p className="font-mono-tech text-xs tracking-[0.35em] text-[#00B0FF] uppercase mb-4">04 — The Process</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white" data-testid="how-it-works-headline">
            HOW LAUNCHLAB <span className="text-gradient-saffron">BUILDS A BUSINESS</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed">
            Six deliberate steps from first conversation to scaling venture. Select any step for the full breakdown.
          </p>
        </motion.div>

        <div className="relative">
          <div className="hidden lg:block absolute top-[52px] left-0 right-0 h-px bg-gradient-to-r from-[#FF8800] via-[#00B0FF] to-[#00E676] opacity-30" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-4">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.num}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.12 }}
                className="relative"
                data-testid={`process-step-${s.num}`}
              >
                <button
                  onClick={() => setSelected(s)}
                  data-testid={`process-step-button-${s.num}`}
                  className="w-full text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8800] rounded-2xl"
                >
                  <div
                    className="w-[104px] h-[104px] rounded-2xl glass flex flex-col items-center justify-center mx-auto lg:mx-0 relative z-10 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-1"
                    style={{ boxShadow: `0 0 30px ${s.accent}18` }}
                  >
                    <s.icon className="w-6 h-6 mb-1.5" style={{ color: s.accent }} strokeWidth={1.8} />
                    <span className="font-mono-tech text-[10px] tracking-[0.25em] text-slate-500">STEP</span>
                    <span className="font-display font-extrabold text-lg" style={{ color: s.accent }}>{s.num}</span>
                  </div>
                  <div className="mt-5 text-center lg:text-left">
                    <h3 className="font-display font-bold tracking-[0.14em] text-white group-hover:text-[#FF8800] transition-colors">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <DetailModal
        item={
          selected && {
            Icon: selected.icon,
            title: selected.title,
            kicker: `Step ${selected.num} — The LaunchLab Process`,
            detail: selected.detail,
            bullets: selected.bullets,
            accent: selected.accent,
          }
        }
        onClose={() => setSelected(null)}
      />
    </section>
  );
}
